import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import _ from 'lodash';

import { exchangeSelector, getExchangesEdit, isLoadingExchangeSelector } from 'src/store/exchanges';
import {
  calculatedExchangeSelector,
  calculateOrderExchange,
  orderErrorsSelector,
  setOrderErrors,
  validateCreateOrder,
} from 'src/store/orders';

import {
  CustomButton,
  CustomCheckbox,
  CustomInput,
  CustomLink,
  PageLoader,
  WhiteCard,
} from 'src/components';

import { WarningIcon } from 'src/assets';
import './createExchange.scss';

export const CreateExchange = () => {
  const { t, i18n } = useTranslation('createExchange');

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const [searchParams] = useSearchParams();

  const weGoldId = searchParams.get('we_gold_id');

  const [formData, setFormData] = useState({
    given_sum: '',
    received_sum: '',
    personal_fields: [],
  });

  const exchange = useSelector(exchangeSelector);
  const isLoadingExchange = useSelector(isLoadingExchangeSelector);

  const calculatedExchange = useSelector(calculatedExchangeSelector);

  const orderErrors = useSelector(orderErrorsSelector);

  useEffect(() => {
    const payload = {
      id: params?.id,
      we_gold_id: weGoldId,
      navigate,
    };

    dispatch(getExchangesEdit(payload));

    return () => {
      dispatch(setOrderErrors({}));
    };
  }, [dispatch, navigate, weGoldId, params?.id, i18n.language]);

  useEffect(() => {
    setFormData(exchange);
  }, [exchange]);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, ...calculatedExchange }));
  }, [calculatedExchange]);

  const onSubmitForm = useCallback(
    (event) => {
      event.preventDefault();

      dispatch(
        validateCreateOrder({ payload: { exchange_id: params?.id, ...formData }, navigate }),
      );
    },
    [dispatch, navigate, formData, params?.id],
  );

  const onChangeGivenFieldsInput = useCallback(
    (field_name, index) => (event) => {
      const fields = formData?.details_of_the_currency_being_given || [];

      const updatedFields = fields.map((item) =>
        item.field_name === field_name ? { ...item, name: event.target.value } : item,
      );

      setFormData((prev) => ({ ...prev, details_of_the_currency_being_given: updatedFields }));

      const copiedErrors = _.cloneDeep(orderErrors);
      delete copiedErrors?.[`fields.${index}.name`];

      dispatch(setOrderErrors(copiedErrors));
    },
    [dispatch, formData?.details_of_the_currency_being_given, orderErrors],
  );

  const onChangePaymentFieldsInput = useCallback(
    (field_name, index) => (event) => {
      const fields = formData?.details_of_the_currency_received || [];

      const updatedFields = fields.map((item) =>
        item.field_name === field_name ? { ...item, name: event.target.value } : item,
      );

      setFormData((prev) => ({ ...prev, details_of_the_currency_received: updatedFields }));

      const copiedErrors = _.cloneDeep(orderErrors);
      delete copiedErrors?.[`fields.${index}.name`];

      dispatch(setOrderErrors(copiedErrors));
    },
    [dispatch, formData?.details_of_the_currency_received, orderErrors],
  );

  const onChangePersonalFieldsInput = useCallback(
    (field_name, index) => (event) => {
      const fields = formData?.personal_fields || [];

      const updatedFields = fields.map((item) =>
        item.field_name === field_name ? { ...item, name: event.target.value } : item,
      );

      setFormData((prev) => ({ ...prev, personal_fields: updatedFields }));

      const copiedErrors = _.cloneDeep(orderErrors);
      delete copiedErrors?.[`fields.${index}.name`];

      dispatch(setOrderErrors(copiedErrors));
    },
    [dispatch, formData?.personal_fields, orderErrors],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedDispatch = useCallback(
    _.debounce((payload) => {
      dispatch(
        calculateOrderExchange({
          id: params?.id,
          payload,
        }),
      );
    }, 1000),
    [dispatch, params?.id],
  );

  const onChangeGivenSum = useCallback(
    (event) => {
      setFormData((prev) => ({ ...prev, given_sum: event.target.value }));

      const copiedErrors = _.cloneDeep(orderErrors);
      delete copiedErrors?.given_sum;

      dispatch(setOrderErrors(copiedErrors));

      debouncedDispatch({ given_sum: event.target.value });
    },
    [dispatch, debouncedDispatch, orderErrors],
  );

  const onChangeReceivedSum = useCallback(
    (event) => {
      setFormData((prev) => ({ ...prev, received_sum: event.target.value }));

      const copiedErrors = _.cloneDeep(orderErrors);
      delete copiedErrors?.received_sum;

      dispatch(setOrderErrors(copiedErrors));

      debouncedDispatch({ received_sum: event.target.value });
    },
    [dispatch, debouncedDispatch, orderErrors],
  );

  const renderSubItem = useCallback(
    (text, value) => {
      return (
        <div className="sub-item">
          <div className="sub-item__text">{t(text)}</div>
          {value}
        </div>
      );
    },
    [t],
  );

  const normalizeMinExchangeCode = useCallback(() => {
    if (exchange?.type_min_exchange) {
      if (exchange?.type_min_exchange === 'given') {
        return exchange?.currencyGiven?.code;
      }

      return exchange?.currencyReceived?.code;
    }
  }, [exchange]);

  const normalizeMaxExchangeCode = useCallback(() => {
    if (exchange?.type_max_exchange) {
      if (exchange?.type_max_exchange === 'given') {
        return exchange?.currencyGiven?.code;
      }

      return exchange?.currencyReceived?.code;
    }
  }, [exchange]);

  return (
    <div className="create-exchange">
      {isLoadingExchange ? <PageLoader /> : null}

      <WhiteCard>
        <div className="create-exchange__header">
          {t('from')} <h2>{exchange?.currencyGiven?.name}</h2> {t('to')}{' '}
          <h2>{exchange?.currencyReceived?.name}</h2>
        </div>
        <div className="create-exchange__divider" />
        <div className="create-exchange__subtitle">
          <Trans i18nKey="createExchange:onlineExchange" />
        </div>
        <div className="create-exchange__warning">
          <img src={WarningIcon} alt={WarningIcon} />
          <strong>{t('warning')}</strong>
        </div>

        <form className="create-exchange__form" onSubmit={onSubmitForm}>
          <div className="create-exchange__content">
            <div className="create-exchange__left">
              <div className="create-exchange__left__header">
                <strong>{t('give')}:</strong>
                <h2>{exchange?.currencyGiven?.name}</h2>
              </div>
              <div className="create-exchange__left__content">
                <div>
                  <CustomInput
                    label={t('give')}
                    type="number"
                    step={0.01}
                    value={formData?.given_sum}
                    onChange={onChangeGivenSum}
                    errorMessage={orderErrors?.given_sum}
                  />
                  {renderSubItem(
                    'minExchangeSum',
                    `${exchange.min_exchange} ${normalizeMinExchangeCode()}`,
                  )}
                </div>
                {formData?.details_of_the_currency_being_given?.map((item, index) => (
                  <div key={item?.field_name}>
                    <CustomInput
                      label={item?.field_translate || 'Translation does not exist'}
                      value={item?.name}
                      onChange={onChangeGivenFieldsInput(item?.field_name, index)}
                      errorMessage={orderErrors?.[`fields.${index}.name`]}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className="create-exchange__right">
              <div className="create-exchange__right__header">
                <strong>{t('receive')}:</strong>
                <h2>{exchange?.currencyReceived?.name}</h2>
              </div>
              <div className="create-exchange__right__content">
                <div>
                  <CustomInput
                    label={t('receive')}
                    type="number"
                    step={0.01}
                    value={formData?.received_sum}
                    onChange={onChangeReceivedSum}
                    errorMessage={orderErrors?.received_sum}
                  />
                  {renderSubItem(
                    'maxExchangeSum',
                    `${exchange.max_exchange} ${normalizeMaxExchangeCode()}`,
                  )}
                  {renderSubItem(
                    'exchangeRate',
                    `${exchange?.rate_given_currency} ${exchange?.currencyGiven?.code} → ${exchange?.rate_received_currency} ${exchange?.currencyReceived?.code}`,
                  )}
                  {renderSubItem(
                    'reserve',
                    `${exchange?.currencyReceived?.reserve} ${exchange?.currencyReceived?.code}`,
                  )}
                </div>
                {formData?.details_of_the_currency_received?.map((item, index) => (
                  <div key={item?.field_name}>
                    <CustomInput
                      label={item?.field_translate || 'Translation does not exist'}
                      value={item?.name}
                      onChange={onChangePaymentFieldsInput(item?.field_name, index)}
                      errorMessage={orderErrors?.[`fields.${index}.name`]}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="create-exchange__divider" />
          <div className="create-exchange__form__content">
            {formData?.personal_fields?.map((item, index) => (
              <CustomInput
                key={item?.field_name}
                label={item?.field_translate}
                value={item?.name}
                onChange={onChangePersonalFieldsInput(
                  item?.field_name,
                  index + exchange?.details_of_the_currency_received?.length,
                )}
                errorMessage={
                  orderErrors?.[
                    `fields.${index + exchange?.details_of_the_currency_received?.length}.name`
                  ]
                }
              />
            ))}
          </div>
          <div className="create-exchange__divider" />
          <div className="create-exchange__form__footer">
            <CustomButton title={t('title:continue')} variant="filled" />
            <div className="create-exchange__form__checkbox">
              <CustomCheckbox />
              <span>
                {t('agreed')} <CustomLink title={t('exchangeRules')} /> {t('and')}{' '}
                <CustomLink title={t('amlRules')} />
              </span>
            </div>
          </div>
        </form>
      </WhiteCard>
      <div className="create-exchange__instructions">
        <WhiteCard>
          <h2>
            {t('instructionsExchange')} {exchange?.currencyGiven?.name} {t('to')}{' '}
            {exchange?.currencyReceived?.name}
          </h2>
          <h4>{t('forExchange')}</h4>
          <ol>
            <li>{t('li1')}</li>
            <li>{t('li2')}</li>
            <li>{t('li3')}</li>
            <li>{t('li4')}</li>
          </ol>
          <div className="create-exchange__instructions__content">{t('instructionsContent')}</div>
        </WhiteCard>
      </div>
    </div>
  );
};
